import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

interface Profile {
  id: string;
  username: string;
  name: string;
  image?: string;
}

interface FollowersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  defaultTab?: 'followers' | 'following';
}

export function FollowersDialog({ open, onOpenChange, userId, defaultTab = 'followers' }: FollowersDialogProps) {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [followers, setFollowers] = useState<Profile[]>([]);
  const [following, setFollowing] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab);
      loadFollowers();
      loadFollowing();
    }
  }, [open, userId, defaultTab]);

  const loadFollowers = async () => {
    setLoading(true);
    try {
      console.log('[FollowersDialog] Loading followers for userId:', userId);
      
      // Get follower IDs - using correct column names: follower_id, following_id
      const { data: followData, error: followError } = await supabase
        .from('follows')
        .select('follower_id')
        .eq('following_id', userId)
        .eq('status', 'accepted');

      if (followError) {
        console.error('[FollowersDialog] Error fetching follows:', {
          error: followError,
          code: followError.code,
          message: followError.message,
          details: followError.details,
          hint: followError.hint,
          userId
        });
        throw followError;
      }

      console.log('[FollowersDialog] Found followers:', followData?.length || 0);

      if (!followData || followData.length === 0) {
        setFollowers([]);
        return;
      }

      // Get profiles for followers
      const followerIds = followData.map(f => f.follower_id);
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, username, name, image')
        .in('id', followerIds);

      if (profileError) {
        console.error('[FollowersDialog] Error fetching profiles:', {
          error: profileError,
          code: profileError.code,
          message: profileError.message
        });
        throw profileError;
      }

      setFollowers(profileData || []);
    } catch (error: any) {
      console.error('[FollowersDialog] loadFollowers error:', {
        error,
        message: error?.message,
        code: error?.code,
        userId,
        stack: error?.stack
      });
    } finally {
      setLoading(false);
    }
  };

  const loadFollowing = async () => {
    setLoading(true);
    try {
      console.log('[FollowersDialog] Loading following for userId:', userId);
      
      // Get following IDs - using correct column names: follower_id, following_id
      const { data: followData, error: followError } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', userId)
        .eq('status', 'accepted');

      if (followError) {
        console.error('[FollowersDialog] Error fetching following:', {
          error: followError,
          code: followError.code,
          message: followError.message,
          details: followError.details,
          hint: followError.hint,
          userId
        });
        throw followError;
      }

      console.log('[FollowersDialog] Found following:', followData?.length || 0);

      if (!followData || followData.length === 0) {
        setFollowing([]);
        return;
      }

      // Get profiles for following
      const followingIds = followData.map(f => f.following_id);
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, username, name, image')
        .in('id', followingIds);

      if (profileError) {
        console.error('[FollowersDialog] Error fetching following profiles:', {
          error: profileError,
          code: profileError.code,
          message: profileError.message
        });
        throw profileError;
      }

      setFollowing(profileData || []);
    } catch (error: any) {
      console.error('[FollowersDialog] loadFollowing error:', {
        error,
        message: error?.message,
        code: error?.code,
        userId,
        stack: error?.stack
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileClick = (username: string) => {
    navigate(`/u/${username}`);
    onOpenChange(false);
  };

  const renderUserList = (users: Profile[]) => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (users.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No {activeTab} yet</p>
        </div>
      );
    }

    return (
      <ScrollArea className="h-[400px]">
        <div className="space-y-2 p-2">
          {users.map((user) => (
            <Button
              key={user.id}
              variant="ghost"
              className="w-full justify-start h-auto p-3"
              onClick={() => handleProfileClick(user.username)}
            >
              <Avatar className="h-10 w-10 mr-3">
                <AvatarImage src={user.image} />
                <AvatarFallback>
                  {user.name?.[0] || user.username?.[0] || '?'}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col items-start">
                <span className="font-medium">{user.name}</span>
                <span className="text-sm text-muted-foreground">@{user.username}</span>
              </div>
            </Button>
          ))}
        </div>
      </ScrollArea>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Connections</DialogTitle>
        </DialogHeader>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'followers' | 'following')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="followers">
              Followers ({followers.length})
            </TabsTrigger>
            <TabsTrigger value="following">
              Following ({following.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="followers">
            {renderUserList(followers)}
          </TabsContent>
          <TabsContent value="following">
            {renderUserList(following)}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
